// ---------------------------------------------------------------------------
// route-skeleton-shapes.ts
// ---------------------------------------------------------------------------
//
// The shape of each route's skeleton, available to the CLIENT at click time.
//
// Why this exists. `loading.tsx` is a server boundary: Next renders it only
// once it has begun fetching the route's payload, so on a slow or dead network
// nothing appears until the request resolves. Measured on a 1.5s-delayed
// payload: the skeleton became visible 3,403ms after the click. Offline it
// never appeared at all — which is the reported symptom, because a click that
// changes nothing reads as a click that did not register.
//
// Navigating is a client-side event. The moment a sidebar item is clicked the
// target route is known, so its skeleton can be rendered from this map on that
// same tick, with no network involved. The server boundary still exists and
// still works — this is what covers the window BEFORE it can.
//
// `route-skeleton-shapes.test.ts` asserts every entry matches the shape that
// route's own `loading.tsx` declares, so the two cannot drift.
// ---------------------------------------------------------------------------

import type { RouteSkeletonShape } from './SkeletonRoute';

export interface RouteSkeletonSpec {
  shape: RouteSkeletonShape;
  stats?: number;
  rows?: number;
  cols?: number;
}

/** Route path (no leading group) to the skeleton that route renders. */
export const ROUTE_SKELETONS: Readonly<Record<string, RouteSkeletonSpec>> = {
  '/analytics': { shape: 'stats', stats: 4 },
  '/analytics/ai-patterns': { shape: 'stats', stats: 7 },
  '/analytics/digital-twin': { shape: 'stats', stats: 4 },
  '/audit-log': { shape: 'stats-table', stats: 4, rows: 10, cols: 6 },
  '/billing/analytics': { shape: 'stats', stats: 4 },
  '/billing/chargebacks': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/billing/reconciliation': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/billing/settlements': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/call-history': { shape: 'table', rows: 10, cols: 7 },
  '/channels': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/channels/ai-competitor': { shape: 'stats', stats: 4 },
  '/channels/ai-travel-agent': { shape: 'stats', stats: 4 },
  '/communications': { shape: 'tabs' },
  '/communications/ai-replies': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/communications/ai-concierge': { shape: 'stats-table', stats: 4, rows: 6, cols: 4 },
  '/communications/voice-concierge': { shape: 'stats', stats: 4 },
  '/compliance': { shape: 'stats', stats: 4 },
  '/compliance/ai-automation': { shape: 'stats', stats: 4 },
  '/corporate': { shape: 'table', rows: 8, cols: 6 },
  '/corporate/[id]': { shape: 'tabs' },
  '/dashboard': { shape: 'stats', stats: 8 },
  '/folio/[id]': { shape: 'form' },
  '/front-desk': { shape: 'board', stats: 4 },
  '/front-desk/ai-ops': { shape: 'stats', stats: 4 },
  '/front-desk/kiosk': { shape: 'form' },
  '/front-desk/online-checkins': { shape: 'table', rows: 4, cols: 3 },
  '/front-desk/upsell': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/groups': { shape: 'table', rows: 8, cols: 6 },
  '/groups/[id]': { shape: 'tabs' },
  '/groups/new': { shape: 'form' },
  '/guests': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/guests/[id]': { shape: 'tabs' },
  '/guests/profiling': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/guests/recommendations': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/guests/reviews': { shape: 'stats', stats: 4 },
  '/guests/sentiment': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/integrations': { shape: 'stats', stats: 8 },
  '/invoices': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/loyalty': { shape: 'tabs' },
  '/loyalty/ai-optimization': { shape: 'stats', stats: 4 },
  '/maintenance': { shape: 'table', rows: 8, cols: 6 },
  '/maintenance/ai-predictive': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/maintenance/assets': { shape: 'table', rows: 8, cols: 6 },
  '/maintenance/preventive': { shape: 'form' },
  '/migration': { shape: 'table', rows: 8, cols: 5 },
  '/night-audit': { shape: 'tabs' },
  '/night-audit/ai-anomaly': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/notifications': { shape: 'tabs' },
  '/onboarding/checklist': { shape: 'form' },
  '/pos': { shape: 'stats', stats: 4 },
  '/pos/[outletId]': { shape: 'form' },
  '/pos/closeout': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/pos/floor-plan': { shape: 'stats', stats: 4 },
  '/pos/menu': { shape: 'table', rows: 8, cols: 5 },
  '/profile': { shape: 'form' },
  '/rate-plans': { shape: 'table', rows: 8, cols: 6 },
  '/rate-plans/autonomous': { shape: 'stats', stats: 4 },
  '/rate-plans/calendar': { shape: 'calendar' },
  '/rate-plans/competitors': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/rate-plans/forecast': { shape: 'stats', stats: 4 },
  '/reports': { shape: 'stats', stats: 8 },
  '/reports/compliance': { shape: 'stats', stats: 4 },
  '/reports/financial': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/reports/flash': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/reports/pace': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/reservations': { shape: 'stats-table', stats: 4, rows: 8, cols: 7 },
  '/reservations/[id]': { shape: 'form' },
  '/reservations/calendar': { shape: 'calendar' },
  '/reservations/new': { shape: 'form' },
  '/room-service': { shape: 'board', stats: 4 },
  '/room-service/kitchen': { shape: 'board', stats: 4 },
  '/room-service/menu': { shape: 'table', rows: 8, cols: 5 },
  '/rooms/board': { shape: 'board', stats: 4 },
  '/rooms/housekeeping': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/rooms/housekeeping/ai-assignment': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/rooms/management': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/settings': { shape: 'tabs' },
  '/settings/ai-energy': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/settings/i18n': { shape: 'form' },
  '/settings/payments': { shape: 'form' },
  '/settings/subscription': { shape: 'stats', stats: 3 },
  '/settings/subscription/success': { shape: 'form' },
  '/settings/taxes': { shape: 'table', rows: 6, cols: 5 },
  '/settings/users': { shape: 'form' },
  '/staff': { shape: 'table', rows: 8, cols: 6 },
  '/staff/ai-scheduler': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/staff/attendance': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/staff/leave': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/staff/payroll': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/staff/performance': { shape: 'stats', stats: 4 },
  '/staff/schedule': { shape: 'table', rows: 8, cols: 7 },
  '/support': { shape: 'form' },

  // ---- Platform admin (/admin) -------------------------------------------
  //
  // These were entirely absent. `beginNavigation` returns early when a route
  // has no shape (`if (!skeletonForPath(href)) return`), so every click in the
  // super admin produced no skeleton at all — and unlike the PMS there is no
  // `loading.tsx` to fall back on either (92 of them under (pms), one under
  // (platform), and that one is the group root). With neither source, a click
  // left the old page on screen with nothing moving, so the operator could not
  // tell whether it had registered and clicked again.
  //
  // Shapes are read off each page's actual component, not guessed: KPI counts
  // come from the `items` array passed to <KPIGrid>, tables from <DataTable>.
  '/admin': { shape: 'stats-table', stats: 5, rows: 6, cols: 5 },
  '/admin/ai-anomalies': { shape: 'stats', stats: 4 },
  '/admin/audit-log': { shape: 'table', rows: 10, cols: 6 },
  '/admin/billing': { shape: 'stats', stats: 4 },
  '/admin/billing/invoices': { shape: 'table', rows: 8, cols: 6 },
  '/admin/billing/payments': { shape: 'table', rows: 8, cols: 6 },
  '/admin/billing/plans': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/admin/billing/usage': { shape: 'table', rows: 8, cols: 6 },
  '/admin/feature-flags': { shape: 'table', rows: 10, cols: 5 },
  '/admin/hotels': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/admin/notifications': { shape: 'table', rows: 10, cols: 5 },
  // Upload form on top, then the catalogue list. Sized from
  // MusicCatalogueManager: one upload card and a list that is short until a
  // platform admin has filled it.
  '/admin/music': { shape: 'table', rows: 6, cols: 4 },
  '/admin/profile': { shape: 'form' },
  '/admin/promotions': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/admin/promotions/policies': { shape: 'form' },
  '/admin/promotions/rates': { shape: 'table', rows: 8, cols: 6 },
  '/admin/promotions/revenue': { shape: 'stats', stats: 4 },
  '/admin/referrals': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/admin/resellers': { shape: 'stats', stats: 5 },
  '/admin/resellers/commissions': { shape: 'table', rows: 8, cols: 6 },
  '/admin/resellers/onboarding': { shape: 'form' },
  '/admin/resellers/tenants': { shape: 'table', rows: 8, cols: 6 },
  '/admin/revenue': { shape: 'stats', stats: 4 },
  '/admin/settings': { shape: 'form' },
  '/admin/subscriptions': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/admin/tickets': { shape: 'stats-table', stats: 5, rows: 8, cols: 6 },
  '/admin/white-label': { shape: 'form' },

  // ---- Chain owner (/chain) ----------------------------------------------
  //
  // Same gap as /admin above: 24 pages, no shapes, no loading.tsx.
  '/chain': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/chain/ai-revenue': { shape: 'stats', stats: 4 },
  '/chain/analytics': { shape: 'stats', stats: 4 },
  '/chain/guests': { shape: 'stats-table', stats: 4, rows: 10, cols: 6 },
  '/chain/notifications': { shape: 'table', rows: 10, cols: 4 },
  '/chain/promotions': { shape: 'stats-table', stats: 4, rows: 6, cols: 5 },
  '/chain/promotions/campaigns': { shape: 'table', rows: 8, cols: 6 },
  '/chain/promotions/credits': { shape: 'table', rows: 8, cols: 5 },
  '/chain/promotions/featured': { shape: 'table', rows: 8, cols: 5 },
  '/chain/profile': { shape: 'form' },
  '/chain/properties': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/chain/rates': { shape: 'table', rows: 8, cols: 7 },
  '/chain/reports': { shape: 'stats', stats: 4 },
  '/chain/reseller': { shape: 'stats', stats: 4 },
  '/chain/reseller/portal': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
  '/chain/roles': { shape: 'table', rows: 8, cols: 5 },
  '/chain/settings': { shape: 'form' },
  '/chain/settings/payments': { shape: 'form' },
  '/chain/staff': { shape: 'stats-table', stats: 4, rows: 10, cols: 6 },
  '/chain/subscription': { shape: 'stats', stats: 4 },
  '/chain/subscription/compare': { shape: 'stats', stats: 3 },
  '/chain/support': { shape: 'form' },
  '/chain/tax-reports': { shape: 'stats-table', stats: 4, rows: 8, cols: 6 },
};

/**
 * The skeleton for a path, falling back to the nearest parent section.
 *
 * A dynamic route (`/guests/abc123`) has no literal entry, so the longest
 * matching prefix is used — the same inheritance the App Router applies to a
 * missing `loading.tsx`, which keeps the two consistent.
 */
export function skeletonForPath(pathname: string): RouteSkeletonSpec | null {
  const direct = ROUTE_SKELETONS[pathname];
  if (direct) return direct;

  let best: RouteSkeletonSpec | null = null;
  let bestLen = -1;
  for (const [route, spec] of Object.entries(ROUTE_SKELETONS)) {
    if (route.length > bestLen && pathname.startsWith(route + '/')) {
      best = spec;
      bestLen = route.length;
    }
  }
  return best;
}
